/**
 * TimeRaceEditor.jsx
 * Admin content editor for lesson_type === 'time_race'
 *
 * Content shape:
 *   {
 *     duration_sec: 60,
 *     questions: [{ question, options, correct_index }]
 *   }
 *
 * Props:
 *   value    — current content object
 *   onChange — (newContent) => void
 */


import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DEFAULT_QUESTION = {
  question: '',
  options: ['', '', '', ''],
  correct_index: 0,
};

export default function TimeRaceEditor({ value, onChange }) {
  const { t } = useLanguage();
  const durationSec = value?.duration_sec ?? 60;
  const questions = Array.isArray(value?.questions) ? value.questions : [];

  const update = (next) => onChange({ ...value, ...next });

  const addQuestion = () =>
    update({ questions: [...questions, { ...DEFAULT_QUESTION, options: ['', '', '', ''] }] });

  const removeQuestion = (idx) =>
    update({ questions: questions.filter((_, i) => i !== idx) });

  const updateQuestion = (idx, field, val) =>
    update({
      questions: questions.map((q, i) => (i === idx ? { ...q, [field]: val } : q)),
    });

  const updateOption = (qIdx, oIdx, val) => {
    const q = questions[qIdx];
    const options = q.options.map((o, i) => (i === oIdx ? val : o));
    updateQuestion(qIdx, 'options', options);
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...questions];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    update({ questions: next });
  };

  const moveDown = (idx) => {
    if (idx === questions.length - 1) return;
    const next = [...questions];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    update({ questions: next });
  };

  return (
    <div className="space-y-3">
      {/* Duration */}
      <div>
        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
          {t('lessonEditor.timeRace.duration')} *
        </label>
        <Input
          type="number"
          min={10}
          max={300}
          value={durationSec}
          onChange={(e) => update({ duration_sec: Number(e.target.value) })}
          className="rounded-xl border-2 border-[var(--border-light)] text-sm w-32"
        />
      </div>

      <div className="h-px bg-[var(--border-light)]" />

      {/* Questions */}
      {questions.length === 0 && (
        <p className="text-xs text-center text-[var(--text-muted)] py-4 border-2 border-dashed border-[var(--border-light)] rounded-xl">
          {t('lessonEditor.common.emptyList')}
        </p>
      )}
      {questions.map((q, idx) => (
        <div
          key={idx}
          className="bg-[var(--bg-app)] border-2 border-[var(--border-light)] rounded-xl p-4 space-y-2"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-[var(--text-muted)]">#{idx + 1}</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                aria-label={t('lessonEditor.common.moveUp')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === questions.length - 1}
                aria-label={t('lessonEditor.common.moveDown')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => removeQuestion(idx)}
                aria-label={t('lessonEditor.timeRace.removeQuestion')}
                className="p-1 rounded hover:bg-red-50 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Question text */}
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
              {t('lessonEditor.timeRace.question')} *
            </label>
            <Input
              value={q.question}
              onChange={(e) => updateQuestion(idx, 'question', e.target.value)}
              placeholder={t('lessonEditor.timeRace.questionPlaceholder')}
              className="rounded-xl border-2 border-[var(--border-light)] text-sm"
            />
          </div>

          {/* Options */}
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
              {t('lessonEditor.timeRace.options')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(q.options || ['', '', '', '']).map((opt, oi) => (
                <div key={oi} className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => updateQuestion(idx, 'correct_index', oi)}
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 transition-colors ${
                      q.correct_index === oi
                        ? 'bg-emerald-500 border-emerald-600'
                        : 'border-gray-300'
                    }`}
                    aria-label={`${t('lessonEditor.timeRace.correctIndex')} ${oi}`}
                    role="radio"
                    aria-checked={q.correct_index === oi}
                  />
                  <Input
                    value={opt}
                    onChange={(e) => updateOption(idx, oi, e.target.value)}
                    placeholder={`${t('lessonEditor.timeRace.option')} ${oi + 1}`}
                    className={`rounded-xl border-2 text-xs h-8 ${
                      q.correct_index === oi
                        ? 'border-emerald-300 bg-emerald-50'
                        : 'border-[var(--border-light)]'
                    }`}
                  />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              {t('lessonEditor.timeRace.correctIndexHint')}
            </p>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addQuestion}
        className="rounded-xl border-2 border-dashed border-[var(--border-heavy)] text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] font-bold w-full"
      >
        <Plus size={14} className="mr-1" /> {t('lessonEditor.timeRace.addQuestion')}
      </Button>
    </div>
  );
}
